import {computed} from 'vue'
import {IThemeVariables} from '@toolify/client/src/modules/theme/types/IThemeVariables'
import {ThemeType} from '@toolify/server/src/services/UserSettingsService/strategies/ThemeUserSettingStrategy/enum/ThemeType'
import {ThemeVariableType} from '@toolify/client/src/modules/theme/enum/ThemeVariableType'
import {ThemeTypeToThemeVariablesMap} from '@toolify/client/src/modules/theme/maps/ThemeTypeToThemeVariablesMap'
import {useCurrentThemeType} from '@toolify/client/src/modules/theme/composables/useCurrentThemeType'
import {useLightModernAppearance} from '@toolify/client/src/modules/theme/composables/useLightModernAppearance'
import {
  LightModernVariantOverridesMap,
} from '@toolify/client/src/modules/theme/strategies/light-modern/LightModernVariantOverridesMap'
import {
  getLightModernDerivedVariables,
} from '@toolify/client/src/modules/theme/functions/getLightModernDerivedVariables'
import {getLightModernNameInkColor} from '@toolify/client/src/modules/theme/functions/getLightModernNameInkColor'

// Podmienia RGB koloru w tokenie cienia na atrament palety (alfa zostaje z tokenu)
function withShadowInk(shadow: string, inkColor: string): string {
  const ink = inkColor.match(/rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)/)
  if (!ink) {
    return shadow
  }
  return shadow.replace(/rgba?\(\s*\d+,\s*\d+,\s*\d+/, `rgba(${ink[1]}, ${ink[2]}, ${ink[3]}`)
}

export function useThemeVariables() {
  const {currentThemeType} = useCurrentThemeType()
  const {paletteVariant: lightModernPaletteVariant} = useLightModernAppearance()

  const themeVariables = computed<IThemeVariables>(() => {
    const baseThemeVariables = ThemeTypeToThemeVariablesMap.get(currentThemeType.value)
    if (currentThemeType.value !== ThemeType.LightModern) {
      return baseThemeVariables
    }
    const merged = {
      ...baseThemeVariables,
      ...LightModernVariantOverridesMap.get(lightModernPaletteVariant.value),
    }
    // po palecie, bo kolor Name to mieszanka jej Content Normal i Content Dim
    merged[ThemeVariableType.DatabaseTableNameColor] = getLightModernNameInkColor(
      merged[ThemeVariableType.ContentNormalColor],
      merged[ThemeVariableType.ContentDimColor],
    )
    const shadowInk = merged[ThemeVariableType.DatabaseTableStickyShadowColor]
    merged[ThemeVariableType.DatabaseTableGroupElevation] = withShadowInk(merged[ThemeVariableType.DatabaseTableGroupElevation], shadowInk)
    merged[ThemeVariableType.DatabaseTableStickyHeaderElevation] = withShadowInk(merged[ThemeVariableType.DatabaseTableStickyHeaderElevation], shadowInk)
    merged[ThemeVariableType.DatabaseTablePinnedColumnElevation] = withShadowInk(merged[ThemeVariableType.DatabaseTablePinnedColumnElevation], shadowInk)
    // obrys grupy jak siatka - po palecie, bo kazda paleta ma wlasny kolor linii komorek
    merged[ThemeVariableType.DatabaseTableCardBorderColor] = merged[ThemeVariableType.DatabaseTableGridColor]
    // wykresy, kanwa automatyzacji, tablica, plakietki filtra - od wartosci tej palety i obrysu karty
    return {
      ...merged,
      ...getLightModernDerivedVariables(merged),
    }
  })

  // TODO: Remove computed
  const getThemeVariable = computed<<T extends ThemeVariableType>(variableName: T) => IThemeVariables[T]>(() => {
    const variables = themeVariables.value
    return function<T extends ThemeVariableType>(variableName: T): IThemeVariables[T] {
      return variables?.[variableName]
    }
  })

  return {
    currentTheme: currentThemeType,
    getThemeVariable,
  }
}
